import { useState } from "react";

const fmt = (n: number) => "$" + Math.round(n).toLocaleString("en-US");

function BlinkingCursor({
  value,
  font,
  fontSize,
  paddingLeft = 0,
}: {
  value: string;
  font: string;
  fontSize: number;
  paddingLeft?: number;
}) {
  return (
    <span
      aria-hidden="true"
      style={{
        position: "absolute",
        top: 0,
        left: 0,
        pointerEvents: "none",
        display: "flex",
        alignItems: "baseline",
        padding: "4px 0 8px 0",
      }}
    >
      <span
        style={{
          fontFamily: font,
          fontSize,
          fontWeight: 400,
          letterSpacing: "0.04em",
          color: "transparent",
          whiteSpace: "pre",
          userSelect: "none",
          paddingLeft,
          lineHeight: 1,
        }}
      >
        {value}
      </span>
      <span
        style={{
          display: "inline-block",
          width: 2,
          height: fontSize * 0.7,
          background: "var(--color-primary)",
          marginLeft: 1,
          borderRadius: 1,
          alignSelf: "center",
          animation: "calc-cursor-blink 1s step-start infinite",
        }}
      />
    </span>
  );
}

type Plan = "free" | "premium";

const MAX_TRAINERS = 500;
const MAX_SESSIONS = 100;
const MAX_RATE = 500;

export function RevenueCalculator() {
  const [trainers, setTrainers] = useState(5);
  const [sessions, setSessions] = useState(5);
  const [rate, setRate] = useState(100);
  const [plan, setPlan] = useState<Plan>("free");
  const [gymSplit, setGymSplit] = useState(20);
  const [focusedInput, setFocusedInput] = useState<string | null>(null);

  const clamp = (val: number, min: number, max: number) => Math.min(max, Math.max(min, val));

  const G = gymSplit / 100;
  const TR = 1 - G;
  const sessPerMonth = (sessions * trainers * 52) / 12;

  const feePerSession = plan === "free" ? 3 : 1;
  const monthlyFixed = plan === "free" ? 0 : 49;
  const planLabel = plan === "free" ? "Free plan" : "Premium plan · $49/mo";

  const ccFeePerSession = rate * 0.0295;

  const NET = rate - ccFeePerSession - feePerSession;
  const totalFeesMonthly = (ccFeePerSession + feePerSession) * sessPerMonth + monthlyFixed;
  const gymMonthly = NET * G * sessPerMonth - monthlyFixed;
  const gymAnnual = NET * G * sessPerMonth * 12 - monthlyFixed * 12;
  const trainerMonthly = trainers > 0 ? NET * TR * sessPerMonth / trainers : 0;
  const trainerAnnual = trainerMonthly * 12;

  const gold = "var(--color-primary)";
  const surface = "var(--hp-card-mid-bg)";
  const border = "var(--hp-calc-border)";
  const muted = "var(--hp-text-muted)";
  const dim = "var(--hp-text-dim)";
  const offWhite = "var(--hp-text-offwhite)";
  const goldDim = "var(--hp-gold-dim)";
  const goldMid = "var(--hp-gold-mid)";
  const mono = "'DM Mono', monospace";
  const bebas = "'Bebas Neue', sans-serif";

  const labelStyle: React.CSSProperties = {
    fontSize: 10,
    fontWeight: 600,
    letterSpacing: "0.16em",
    textTransform: "uppercase",
    color: muted,
    fontFamily: mono,
    display: "block",
    marginBottom: 8,
  };

  const inputStyle: React.CSSProperties = {
    width: "100%",
    background: "transparent",
    border: "none",
    borderBottom: `1px solid ${border}`,
    color: "var(--color-primary)",
    fontFamily: bebas,
    fontSize: 36,
    fontWeight: 400,
    letterSpacing: "0.04em",
    padding: "4px 0 8px 0",
    lineHeight: 1,
    outline: "none",
    MozAppearance: "textfield",
  };

  return (
    <div className="revenue-calc" style={{ width: "100%", maxWidth: 660, fontFamily: mono }}>
      <div style={{ border: `0.5px solid ${border}` }}>
        {/* INPUTS */}
        <div style={{ padding: "28px 28px 24px", borderBottom: `0.5px solid ${border}` }}>
          {/* Input grid */}
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(3, 1fr)",
              gap: "20px 28px",
              marginBottom: 28,
            }}
            className="calc-input-grid"
          >
            <div style={{ display: "flex", flexDirection: "column" }}>
              <label htmlFor="calc-trainers" style={labelStyle}>Number of Coaches</label>
              <div className="calc-input-wrap" style={{ marginTop: "auto" }}>
                <input
                  id="calc-trainers"
                  type="number"
                  value={trainers}
                  min={0}
                  max={MAX_TRAINERS}
                  step={1}
                  aria-label="Number of coaches"
                  style={inputStyle}
                  onFocus={() => setFocusedInput("trainers")}
                  onBlur={() => setFocusedInput(null)}
                  onChange={(e) => setTrainers(clamp(Number(e.target.value) || 0, 0, MAX_TRAINERS))}
                />
                {focusedInput !== "trainers" && (
                  <BlinkingCursor value={String(trainers)} font={bebas} fontSize={36} />
                )}
              </div>
            </div>
            <div style={{ display: "flex", flexDirection: "column" }}>
              <label htmlFor="calc-sessions" style={labelStyle}>Sessions / trainer / week</label>
              <div className="calc-input-wrap" style={{ marginTop: "auto" }}>
                <input
                  id="calc-sessions"
                  type="number"
                  value={sessions}
                  min={0}
                  max={MAX_SESSIONS}
                  step={1}
                  aria-label="Sessions per trainer per week"
                  style={inputStyle}
                  onFocus={() => setFocusedInput("sessions")}
                  onBlur={() => setFocusedInput(null)}
                  onChange={(e) => setSessions(clamp(Number(e.target.value) || 0, 0, MAX_SESSIONS))}
                />
                {focusedInput !== "sessions" && (
                  <BlinkingCursor value={String(sessions)} font={bebas} fontSize={36} />
                )}
              </div>
            </div>
            <div style={{ display: "flex", flexDirection: "column" }}>
              <label htmlFor="calc-rate" style={labelStyle}>Session rate</label>
              <div className="calc-input-wrap" style={{ marginTop: "auto" }}>
                <span
                  style={{
                    position: "absolute",
                    left: 0,
                    bottom: 0,
                    fontSize: 36,
                    fontFamily: bebas,
                    fontWeight: 400,
                    letterSpacing: "0.04em",
                    color: dim,
                    pointerEvents: "none",
                    lineHeight: 1,
                    padding: "4px 0 8px 0",
                  }}
                  aria-hidden="true"
                >
                  $
                </span>
                <input
                  id="calc-rate"
                  type="number"
                  value={rate}
                  min={0}
                  max={MAX_RATE}
                  step={5}
                  aria-label="Session rate in dollars"
                  style={{ ...inputStyle, paddingLeft: 18 }}
                  onFocus={() => setFocusedInput("rate")}
                  onBlur={() => setFocusedInput(null)}
                  onChange={(e) => setRate(clamp(Number(e.target.value) || 0, 0, MAX_RATE))}
                />
                {focusedInput !== "rate" && (
                  <BlinkingCursor value={String(rate)} font={bebas} fontSize={36} paddingLeft={18} />
                )}
              </div>
            </div>
          </div>

          {/* Plan toggle */}
          <div style={{ marginBottom: 24 }}>
            <div style={{ ...labelStyle, marginBottom: 10 }}>Open Mat plan</div>
            <div className="calc-plan-options" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
              {(["free", "premium"] as Plan[]).map((p) => {
                const isActive = plan === p;
                return (
                  <button
                    key={p}
                    type="button"
                    aria-pressed={isActive}
                    onClick={() => setPlan(p)}
                    style={{
                      border: `0.5px solid ${isActive ? gold : border}`,
                      background: isActive ? goldDim : "transparent",
                      padding: "14px 16px",
                      cursor: "pointer",
                      transition: "border-color 0.15s, background 0.15s",
                      userSelect: "none",
                      textAlign: "left",
                      width: "100%",
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        marginBottom: 5,
                      }}
                    >
                      <span
                        style={{
                          fontFamily: bebas,
                          fontSize: 20,
                          letterSpacing: "0.06em",
                          color: isActive ? gold : "var(--hp-heading)",
                          display: "flex",
                          alignItems: "center",
                          gap: 6,
                        }}
                      >
                        <span
                          style={{
                            width: 8,
                            height: 8,
                            borderRadius: "50%",
                            border: `1px solid ${isActive ? gold : dim}`,
                            background: isActive ? gold : "transparent",
                            display: "inline-block",
                            flexShrink: 0,
                          }}
                        />
                        {p === "free" ? "Free" : "Premium"}
                      </span>
                      <span
                        style={{
                          fontSize: 9,
                          fontWeight: 600,
                          letterSpacing: "0.12em",
                          textTransform: "uppercase",
                          padding: "3px 8px",
                          border: `0.5px solid ${isActive ? goldMid : dim}`,
                          color: isActive ? "var(--hp-gold-a60)" : dim,
                          fontFamily: mono,
                        }}
                      >
                        {p === "free" ? "Current" : "Recommended"}
                      </span>
                    </div>
                    <div
                      style={{
                        fontSize: 11,
                        fontStyle: "italic",
                        color: isActive ? "var(--hp-gold-a55)" : muted,
                        lineHeight: 1.5,
                        fontFamily: mono,
                      }}
                    >
                      {p === "free"
                        ? "$3 per session · No monthly fee"
                        : "$49/month + $1 per session · Lowest per-session cost"}
                    </div>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Split slider */}
          <div style={{ display: "flex", alignItems: "center", gap: 20 }} className="split-row-calc">
            <div style={{ minWidth: 150 }}>
              <div style={{ ...labelStyle, marginBottom: 4 }}>Revenue split</div>
              <div style={{ display: "flex", gap: 6, alignItems: "baseline" }}>
                <span style={{ fontFamily: bebas, fontSize: 28, color: gold }}>{gymSplit}%</span>
                <span style={{ fontSize: 12, color: dim, fontFamily: mono }}>gym /</span>
                <span style={{ fontFamily: bebas, fontSize: 20, color: dim }}>{100 - gymSplit}%</span>
                <span style={{ fontSize: 12, color: dim, fontFamily: mono }}>trainer</span>
              </div>
            </div>
            <label htmlFor="calc-split" style={{ position: "absolute", width: 1, height: 1, overflow: "hidden", clip: "rect(0,0,0,0)", whiteSpace: "nowrap" }}>
              Gym revenue split percentage
            </label>
            <input
              id="calc-split"
              type="range"
              min={5}
              max={50}
              step={5}
              value={gymSplit}
              aria-label="Gym revenue split percentage"
              onChange={(e) => setGymSplit(Number(e.target.value))}
              style={{ flex: 1, accentColor: gold, height: 2, cursor: "pointer" }}
            />
          </div>
        </div>

        {/* RESULTS */}
        <div style={{ background: surface, padding: 28 }}>
          <div
            style={{
              fontSize: 10,
              fontWeight: 600,
              letterSpacing: "0.16em",
              textTransform: "uppercase",
              color: dim,
              marginBottom: 18,
              fontFamily: mono,
            }}
          >
            Your gym's revenue breakdown
          </div>

          {/* Top results grid */}
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "2fr 1fr 1fr",
              border: `0.5px solid ${border}`,
              marginBottom: 1,
            }}
            className="results-top-calc"
          >
            {/* Monthly gym revenue */}
            <div
              className="results-featured-cell"
              style={{
                padding: "18px 16px",
                borderRight: `0.5px solid ${border}`,
              }}
            >
              <div
                style={{
                  fontSize: 9,
                  fontWeight: 600,
                  letterSpacing: "0.16em",
                  textTransform: "uppercase",
                  color: dim,
                  marginBottom: 6,
                  fontFamily: mono,
                }}
              >
                Monthly gym revenue
              </div>
              <div style={{ fontFamily: bebas, fontSize: 44, color: gold, lineHeight: 1, letterSpacing: "0.02em" }}>
                {fmt(gymMonthly)}
              </div>
              <div style={{ fontSize: 10, color: dim, marginTop: 3, fontStyle: "italic", fontFamily: mono }}>
                after Open Mat fees · your share
              </div>
            </div>
            {/* Sessions / month */}
            <div style={{ padding: "18px 16px", borderRight: `0.5px solid ${border}` }}>
              <div
                style={{
                  fontSize: 9,
                  fontWeight: 600,
                  letterSpacing: "0.16em",
                  textTransform: "uppercase",
                  color: dim,
                  marginBottom: 6,
                  fontFamily: mono,
                }}
              >
                Sessions / month
              </div>
              <div style={{ fontFamily: bebas, fontSize: 34, color: "var(--hp-heading)", lineHeight: 1, letterSpacing: "0.02em" }}>
                {Math.round(sessPerMonth).toLocaleString()}
              </div>
              <div style={{ fontSize: 10, color: dim, marginTop: 3, fontStyle: "italic", fontFamily: mono }}>
                across all trainers
              </div>
            </div>
            {/* Net / session */}
            <div style={{ padding: "18px 16px" }}>
              <div
                style={{
                  fontSize: 9,
                  fontWeight: 600,
                  letterSpacing: "0.16em",
                  textTransform: "uppercase",
                  color: dim,
                  marginBottom: 6,
                  fontFamily: mono,
                }}
              >
                Net / session
              </div>
              <div style={{ fontFamily: bebas, fontSize: 34, color: "var(--hp-heading)", lineHeight: 1, letterSpacing: "0.02em" }}>
                {fmt(NET)}
              </div>
              <div style={{ fontSize: 10, color: dim, marginTop: 3, fontStyle: "italic", fontFamily: mono }}>
                {plan === "free" ? "after CC fees & $3 booking" : "after CC fees & $1 booking"}
              </div>
            </div>
          </div>

          {/* Annual bar */}
          <div
            style={{
              border: `0.5px solid ${goldMid}`,
              background: goldDim,
              padding: "18px 20px",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 12,
              marginBottom: 1,
            }}
          >
            <div>
              <div
                style={{
                  fontSize: 10,
                  fontWeight: 600,
                  letterSpacing: "0.16em",
                  textTransform: "uppercase",
                  color: "var(--hp-gold-a55)",
                  marginBottom: 3,
                  fontFamily: mono,
                }}
              >
                Gym annual revenue
              </div>
              <div style={{ fontSize: 11, fontStyle: "italic", color: "var(--hp-gold-a40)", fontFamily: mono }}>
                Your cut · 52 weeks · {planLabel}
              </div>
            </div>
            <div
              style={{
                fontFamily: bebas,
                fontSize: 48,
                color: gold,
                letterSpacing: "0.02em",
                lineHeight: 1,
                whiteSpace: "nowrap",
              }}
            >
              {fmt(gymAnnual)}
            </div>
          </div>

          {/* Breakdown rows */}
          <div style={{ border: `0.5px solid ${border}`, borderTop: "none" }}>
            {[
              { key: "Open Mat fees / month", val: fmt(totalFeesMonthly) },
              { key: "Trainer payout / month (each)", val: fmt(trainerMonthly) },
              { key: "Trainer annual earnings (each)", val: fmt(trainerAnnual) },
            ].map((row, i, arr) => (
              <div
                key={row.key}
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  padding: "10px 16px",
                  borderBottom: i < arr.length - 1 ? `0.5px solid var(--hp-row-divider)` : "none",
                }}
              >
                <span style={{ fontSize: 11, color: muted, fontFamily: mono }}>{row.key}</span>
                <span
                  style={{
                    fontFamily: bebas,
                    fontSize: 18,
                    color: offWhite,
                    letterSpacing: "0.04em",
                  }}
                >
                  {row.val}
                </span>
              </div>
            ))}
          </div>

          <p
            style={{
              fontSize: 11,
              fontStyle: "italic",
              color: dim,
              marginTop: 16,
              fontFamily: mono,
            }}
          >
            Assumes 52 weeks/year · CC fee ~2.95% · Open Mat fee deducted before split
          </p>
        </div>
      </div>

      <style>{`
        .revenue-calc input[type="number"]::-webkit-outer-spin-button,
        .revenue-calc input[type="number"]::-webkit-inner-spin-button { -webkit-appearance: none; }
        .revenue-calc input[type="number"] { -moz-appearance: textfield; }
        @media (max-width: 580px) {
          .calc-input-grid { grid-template-columns: 1fr 1fr !important; }
          .results-top-calc { grid-template-columns: 1fr 1fr !important; }
          .results-top-calc .results-featured-cell { grid-column: 1 / -1; border-right: none !important; border-bottom: 0.5px solid var(--hp-calc-border); }
          .results-top-calc .results-featured-cell ~ div:last-child { border-right: none !important; }
          .split-row-calc { flex-direction: column; align-items: flex-start; }
          .split-row-calc input[type="range"] { width: 100%; }
        }
        @media (max-width: 400px) {
          .calc-input-grid { grid-template-columns: 1fr !important; }
          .calc-plan-options { grid-template-columns: 1fr !important; }
        }
      `}</style>
    </div>
  );
}
